import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

export const useLoyaltyContentFallbacks = (availablePoints: number) => {
  const { formatMessage, formatNumber } = useIntl();

  return useMemo(
    () => ({
      faqTitleXs: formatMessage(messages.faqTitleXs),
      faqSubtitleXs: formatMessage(messages.faqSubtitleXs),
      faqTitleSm: formatMessage(messages.faqTitleSm),
      faqSubtitleSm: formatMessage(messages.faqSubtitleSm),
      featuredRewardOrderCtaLabel: formatMessage(messages.featuredRewardOrderCtaLabel),
      featuredRewardDetailsLinkLabel: formatMessage(messages.featuredRewardDetailsLinkLabel),
      bagHeaderLoggedOut: formatMessage(messages.bagHeaderLoggedOut),
      bagHeaderNewUser: formatMessage(messages.bagHeaderNewUser),
      bagHeaderRecurringUser: formatMessage(messages.bagHeaderRecurringUser, {
        points: formatNumber(availablePoints),
      }),
      bagHeaderSubtitle: formatMessage(messages.bagHeaderSubtitle),
      bagHeaderLoggedOutImageMessage: formatMessage(messages.bagHeaderLoggedOutImageMessage),
      bagDiscountBody: formatMessage(messages.bagDiscountBody),
      orderStatusRewardDiscountNote: formatMessage(messages.orderStatusRewardDiscountNote),
      earnPointsTitle: formatMessage(messages.earnPointsTitle),
      earnPointsDescription: formatMessage(messages.earnPointsDescription),
      redeemRewardsTitle: formatMessage(messages.redeemRewardsTitle),
      redeemRewardsDescription: formatMessage(messages.redeemRewardsDescription),
      lookOutForOffersTitle: formatMessage(messages.lookOutForOffersTitle),
      lookOutForOffersDescription: formatMessage(messages.lookOutForOffersDescription),
      homeChallengesEmptyStateTitle: formatMessage(messages.homeChallengesEmptyStateTitle),
      homeChallengesEmptyStateBody: formatMessage(messages.homeChallengesEmptyStateBody),
      homeChallengesEmptyStateCtaXS: formatMessage(messages.homeChallengesEmptyStateCtaXS),
      homeChallengesEmptyStateCtaSM: formatMessage(messages.homeChallengesEmptyStateCtaSM),
      homeRewardsEmptyStateTitle: formatMessage(messages.homeRewardsEmptyStateTitle),
      homeRewardsEmptyStateBody: formatMessage(messages.homeRewardsEmptyStateBody),
      returnHeroTitle: formatMessage(messages.returnHeroTitle),
      loyaltyBenefitsTitle: formatMessage(messages.loyaltyBenefitsTitle),
      loyaltyBenefitsSubtitle: formatMessage(messages.loyaltyBenefitsSubtitle),
      unlockableRewardsTitle: formatMessage(messages.unlockableRewardsTitle),
      unlockableRewardsSubtitleNoPoints: formatMessage(messages.unlockableRewardsSubtitleNoPoints),
      unlockableRewardsSubtitleDefault: formatMessage(messages.unlockableRewardsSubtitleDefault),
      unlockableRewardsSubtitleTooManyPoints: formatMessage(
        messages.unlockableRewardsSubtitleTooManyPoints,
      ),
    }),
    [availablePoints, formatMessage, formatNumber],
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  faqTitleXs: {
    defaultMessage: 'Frequently asked SG rewards questions',
    description: 'Loyalty Content | FAQ | Title XS',
  },
  faqSubtitleXs: {
    defaultMessage: 'Everything you need to know about SG rewards',
    description: 'Loyalty Content | FAQ | Subtitle XS',
  },
  faqTitleSm: {
    defaultMessage: 'Frequently asked SG rewards questions',
    description: 'Loyalty Content | FAQ | Title SM',
  },
  faqSubtitleSm: {
    defaultMessage: 'Everything you need to know about SG rewards',
    description: 'Loyalty Content | FAQ | Subtitle SM',
  },
  featuredRewardOrderCtaLabel: {
    defaultMessage: 'Order to redeem',
    description: 'Loyalty Content | Featured Reward | Order CTA',
  },
  featuredRewardDetailsLinkLabel: {
    defaultMessage: 'Details',
    description: 'Loyalty Content | Featured Reward | Details Link',
  },
  bagHeaderLoggedOut: {
    defaultMessage: 'Complete your order + earn',
    description: 'Loyalty Content | Bag | Title Logged Out',
  },
  bagHeaderNewUser: {
    defaultMessage: 'You’re about to get rewarded',
    description: 'Loyalty Content | Bag | Title New User',
  },
  bagHeaderRecurringUser: {
    defaultMessage: 'You have {points} points',
    description: 'Loyalty Content | Bag | Title Recurring User',
  },
  bagHeaderSubtitle: {
    defaultMessage:
      'Apply one available reward to this order. Required items must be in your bag to apply a reward.',
    description: 'Loyalty Content | Bag | Subtitle',
  },
  bagHeaderLoggedOutImageMessage: {
    defaultMessage: 'Get a free side of Ripple Fries when you create an account',
    description: 'Loyalty Content | Bag | Image Text',
  },
  bagDiscountBody: {
    defaultMessage: 'Reward discount applied before taxes and fees.',
    description: 'Loyalty Content | Bag | Discount body',
  },
  earnPointsTitle: {
    defaultMessage: 'Earn points',
    description: 'Loyalty Content | Earn points | Title',
  },
  earnPointsDescription: {
    defaultMessage: 'Scan your QR code at checkout to earn 10 points for every $1 spent',
    description: 'Loyalty Content | Earn points | Description',
  },
  redeemRewardsTitle: {
    defaultMessage: 'Redeem rewards',
    description: 'Loyalty Content | Redeem rewards | Title',
  },
  redeemRewardsDescription: {
    defaultMessage: 'Use points to unlock rewards and free Sweetgreen',
    description: 'Loyalty Content | Redeem rewards | Description',
  },
  lookOutForOffersTitle: {
    defaultMessage: 'Look out for offers',
    description: 'Loyalty Content | Look out for offers | Title',
  },
  lookOutForOffersDescription: {
    defaultMessage: 'Look out for offers to earn more points and special rewards',
    description: 'Loyalty Content | Look out for offers | Description',
  },
  homeChallengesEmptyStateTitle: {
    defaultMessage: 'Free delicious deals ahead',
    description: 'Loyalty Content | Home | Challenges | Empty State | Title',
  },
  homeChallengesEmptyStateBody: {
    defaultMessage: 'Earn 10 points for every $1',
    description: 'Loyalty Content | Home | Challenges | Empty State | Body',
  },
  homeChallengesEmptyStateCtaXS: {
    defaultMessage: 'Start order',
    description: 'Loyalty Content | Home | Challenges | Empty State | CTA XS',
  },
  homeChallengesEmptyStateCtaSM: {
    defaultMessage: 'Start an order',
    description: 'Loyalty Content | Home | Challenges | Empty State | CTA SM',
  },
  homeRewardsEmptyStateTitle: {
    defaultMessage: 'Rewards',
    description: 'Loyalty Content | Home | Rewards | Empty State | Title',
  },
  homeRewardsEmptyStateBody: {
    defaultMessage: 'No bonus rewards available at the moment.',
    description: 'Loyalty Content | Home | Rewards | Empty State | Body',
  },
  orderStatusRewardDiscountNote: {
    defaultMessage: 'Reward discount applied before taxes and fees.',
    description: 'Loyalty Content | Order Status | Reward discount note',
  },
  returnHeroTitle: {
    defaultMessage: 'The easiest way to earn free Sweetgreen.',
    description: 'Loyalty Content | Return hero | Title',
  },
  loyaltyBenefitsTitle: {
    defaultMessage: 'Order to earn',
    description: 'Loyalty Content | Loyalty benefits | Title',
  },
  loyaltyBenefitsSubtitle: {
    defaultMessage: 'Redeem a reward!',
    description: 'Loyalty Content | Loyalty benefits | Subtitle',
  },
  unlockableRewardsTitle: {
    defaultMessage: 'Unlock-able rewards',
    description: 'Loyalty Content | Unlockable rewards | Title',
  },
  unlockableRewardsSubtitleNoPoints: {
    defaultMessage: 'Place your first order to unlock',
    description: 'Loyalty Content | Unlockable rewards | Subtitle No Points',
  },
  unlockableRewardsSubtitleDefault: {
    defaultMessage: 'A free (name) is only (points) pts away!',
    description: 'Loyalty Content | Unlockable rewards | Subtitle Default',
  },
  unlockableRewardsSubtitleTooManyPoints: {
    defaultMessage: "It's time to spend your points!",
    description: 'Loyalty Content | Unlockable rewards | Subtitle Too Many Points',
  },
});
